// What a proposal is ABOUT, in one line — the rail's title, the descriptor's input, and the
// trigram key the near-duplicate detector compares.
//
// This lived twice, once in ProposalsPage.tsx and once in descriptorQueue.ts, as the same
// expression: statement ?? name ?? topic ?? question ?? title. When the amendment kinds landed
// (update_deliverable and its three siblings) both copies returned EMPTY for them, because an
// amendment payload carries none of those keys — it carries the ref of the thing it amends, the
// fields it would change, and a reason. The rail showed "UPDATE_DELIVERABLE #464" with no title
// and the descriptor queue had nothing to summarise. One expression, one place, from here on.

/** The payload keys an amendment travels under, per entity — mirrors services/amendable.ts. */
export const AMENDMENT_REF_KEYS = [
  'deliverable_ref', 'deliverable_item_ref', 'acceptance_criterion_ref', 'test_case_ref',
] as const;

export const isAmendmentKind = (kind: string): boolean => kind.startsWith('update_')
  && kind !== 'update_requirement'; // that one carries a full statement and reads like a create

/** The ref an amendment names, or null when the payload carries none. */
export function amendmentRef(payload: Record<string, unknown>): string | null {
  for (const key of AMENDMENT_REF_KEYS) {
    const v = payload[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

/** The fields an amendment would actually change — payload minus its ref and its reason. */
export function amendedFields(payload: Record<string, unknown>): string[] {
  const skip = new Set<string>([...AMENDMENT_REF_KEYS, 'reason', 'deliverable_id']);
  return Object.keys(payload).filter((k) => !skip.has(k) && payload[k] !== undefined);
}

/**
 * The DELIVERABLE a proposal lands in, or null when it does not touch one. This is the hinge
 * of the cross-deliverable story: a workbench conversation records the deliverable it was
 * launched from (extraction_runs.context_ref), and a proposal whose landing deliverable
 * differs from that context is CROSS-DELIVERABLE — it surfaces in the TARGET bench, enters
 * the review queue despite being conversation-born, and holding it records a dependency link.
 * The SQL twin of this function lives in server/utils/proposalQueue.ts
 * (crossDeliverableTargetExpr) — change both or the queue and the benches disagree.
 */
export function proposalDeliverableRef(kind: string, payload: Record<string, unknown>): string | null {
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const itemParent = (v: unknown): string | null => {
    // FIRST dot, and a dotless ref is NOT an item ref (null, not a guess) — exactly what the
    // SQL twin's SPLIT_PART + POSITION guard computes, so both sides agree on every input.
    const ref = str(v);
    if (!ref) return null;
    const dot = ref.indexOf('.');
    return dot > 0 ? ref.slice(0, dot) : null;   // DEL-01.2 → DEL-01
  };
  switch (kind) {
    case 'create_deliverable_item': return str(payload.deliverable_ref);
    case 'update_deliverable': return str(payload.deliverable_ref);
    case 'update_deliverable_item': return itemParent(payload.deliverable_item_ref);
    case 'provisional': return payload.entity_type === 'deliverable' ? str(payload.entity_ref) : null;
    default: return null;
  }
}

/**
 * One line naming the proposal. For an amendment that is the REF plus what would change —
 * "DEL-04 · description, satisfies_refs" — because the ref alone does not say why it is in the
 * queue, and the reason alone does not say what it touches.
 */
export function proposalSubject(kind: string, payload: Record<string, unknown>): string {
  if (isAmendmentKind(kind)) {
    const ref = amendmentRef(payload);
    const fields = amendedFields(payload);
    if (ref && fields.length) return `${ref} · ${fields.join(', ')}`;
    if (ref) return ref;
  }
  const y = payload;
  return String(y.statement ?? y.name ?? y.topic ?? y.question ?? y.title ?? '');
}
